import type { Result, AppError } from '@shared/utils';
import { ok, err } from '@shared/utils';
import type { BoardMember } from '../interfaces/BoardMember';

/**
 * @group Public
 * @summary Extracts initials from a board member's name
 * 
 * Takes a board member object and extracts their initials from their name.
 * The name should already be clean (without titles) since title is stored separately.
 * 
 * @param member - Board member object with name and title
 * @returns Result containing initials (e.g., "NJ" or "MW") or an error
 * 
 * @example
 * ```typescript
 * const result = getInitials(member);
 * result.match({
 *   ok: (initials) => console.log(initials), // "NJ"
 *   err: (error) => console.error(error),
 * });
 * ```
 */
export function getInitials(member: BoardMember): Result<string, AppError> {
  if (!member.name || typeof member.name !== 'string' || member.name.trim().length === 0) {
    return err({
      errorCode: 'INVALID_NAME',
      errorMessage: 'Member name is required and must be a non-empty string',
    });
  }

  // Name should already be clean (without titles) since title is stored separately
  // Split by spaces and get first letter of each word
  const words = member.name.trim().split(/\s+/).filter(word => word.length > 0);
  
  if (words.length === 0) {
    return ok('?');
  }
  
  if (words.length === 1) {
    return ok(words[0].charAt(0).toUpperCase());
  }
  
  // Get first letter of first and last word
  const initials = (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
  return ok(initials);
}
